<script setup lang="ts">
import InertiaFormField from '@/components/InertiaFormField.vue'
import NaturalPersonController from '@controllers/NaturalPersonController'
import { useForm } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import NaturalPersonType = App.Enums.User.NaturalPersonType

const { t } = useI18n()

const visible = defineModel<boolean>('visible', { required: true })

const props = defineProps<{
  availableTypes: NaturalPersonType[]
}>()

type NaturalPersonForm = {
  first_name: string | null
  last_name: string | null
  type: NaturalPersonType | null
  has_community_of_accrued_gains?: boolean
  has_marital_contract?: boolean
  has_separate_assessment?: boolean
  living_in_household?: boolean
  receives_child_support?: boolean
}

const form = useForm<NaturalPersonForm>({
  first_name: null,
  last_name: null,
  type: null,
  has_community_of_accrued_gains: true,
  has_separate_assessment: false,
  living_in_household: false,
  receives_child_support: false,
})

const onFormSubmit = () => {
  form
    .transform(({ has_community_of_accrued_gains, ...data }) => ({
      ...data,
      has_marital_contract:
        data.type === 'partner' && typeof has_community_of_accrued_gains === 'boolean'
          ? !has_community_of_accrued_gains
          : undefined,
      has_separate_assessment: data.type === 'partner' ? data.has_separate_assessment : undefined,
      living_in_household: data.type === 'child' ? data.living_in_household : undefined,
      receives_child_support: data.type === 'child' ? data.receives_child_support : undefined,
    }))
    .post(NaturalPersonController.store().url, {
      preserveScroll: true,
      preserveState: true,

      onSuccess: () => {
        form.reset()
        visible.value = false
      },
    })
}

const resetForm = () => {
  form.reset()
  form.clearErrors()
}
</script>

<template>
  <Dialog v-model:visible="visible" modal :header="t('title')" class="w-full max-w-lg" @after-hide="resetForm">
    <span class="text-surface-500 mb-8 block">
      {{ t('subtitle') }}
    </span>

    <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
      <InertiaFormField :label="t('form.first_name')" :error="form.errors.first_name">
        <InputText v-model="form.first_name" name="first_name" :invalid="!!form.errors.first_name" />
      </InertiaFormField>

      <InertiaFormField :label="t('form.last_name')" :error="form.errors.last_name">
        <InputText v-model="form.last_name" name="last_name" :invalid="!!form.errors.last_name" />
      </InertiaFormField>

      <div class="md:col-span-2">
        <InertiaFormField :label="t('form.type')" :error="form.errors.type">
          <SelectButton
            v-model="form.type"
            :options="props.availableTypes"
            :option-label="(option) => t(`form.typeOptions.${option}`)"
            :allow-empty="false"
            :invalid="!!form.errors.type" />
        </InertiaFormField>
      </div>
    </div>

    <transition-group name="exdtra" tag="div">
      <div v-if="form.type === 'partner'" class="flex flex-col gap-1">
        <InertiaFormField :error="form.errors.has_marital_contract">
          <div class="flex items-center gap-2">
            <Checkbox
              v-model="form.has_community_of_accrued_gains"
              input-id="has_community_of_accrued_gains"
              binary
              :invalid="!!form.errors.has_marital_contract" />
            <label for="has_community_of_accrued_gains">{{ t('form.has_community_of_accrued_gains') }}</label>
          </div>
        </InertiaFormField>
        <InertiaFormField :error="form.errors.has_separate_assessment">
          <div class="flex items-center gap-2">
            <Checkbox
              v-model="form.has_separate_assessment"
              input-id="has_separate_assessment"
              binary
              :invalid="!!form.errors.has_separate_assessment" />
            <label for="has_separate_assessment">{{ t('form.has_separate_assessment') }}</label>
          </div>
        </InertiaFormField>
      </div>
      <div v-if="form.type === 'child'" class="flex flex-col gap-1">
        <InertiaFormField :error="form.errors.living_in_household">
          <div class="flex items-center gap-2">
            <Checkbox
              v-model="form.living_in_household"
              input-id="living_in_household"
              binary
              :disabled="!!form.receives_child_support"
              :invalid="!!form.errors.living_in_household" />
            <label for="living_in_household" :class="{ 'opacity-50': !!form.receives_child_support }">{{
              t('form.living_in_household')
            }}</label>
          </div>
        </InertiaFormField>
        <InertiaFormField :error="form.errors.receives_child_support">
          <div class="flex items-center gap-2">
            <Checkbox
              v-model="form.receives_child_support"
              input-id="receives_child_support"
              binary
              :disabled="!!form.living_in_household"
              :invalid="!!form.errors.receives_child_support" />
            <label for="receives_child_support" :class="{ 'opacity-50': !!form.living_in_household }">{{
              t('form.receives_child_support')
            }}</label>
          </div>
        </InertiaFormField>
      </div>
    </transition-group>

    <template #footer>
      <Button :label="t('actions.cancel')" text severity="secondary" @click="visible = false" />
      <Button :label="t('actions.save')" autofocus @click="onFormSubmit" />
    </template>
  </Dialog>
</template>

<style scoped>
label {
  cursor: pointer;
}
</style>

<i18n>
{
  "en": {
    "title": "Add person",
    "subtitle": "Add a new person to your household",
    "actions": {
      "cancel": "Cancel",
      "save": "Save"
    },
    "form": {
      "first_name": "First Name",
      "last_name": "Last Name",
      "type": "Type",
      "typeOptions": {
        "partner": "Partner",
        "child": "Child",
        "other": "Other"
      },
      "has_community_of_accrued_gains": "Community of accrued gains",
      "has_separate_assessment": "Separate tax assessment",
      "living_in_household": "Living in household",
      "receives_child_support": "Receives child support"
    }
  },
  "de": {
    "title": "Person hinzufügen",
    "subtitle": "Füge eine neue Person zu deinem Haushalt hinzu",
    "actions": {
      "cancel": "Abbrechen",
      "save": "Speichern"
    },
    "form": {
      "first_name": "Vorname",
      "last_name": "Nachname",
      "type": "Typ",
      "typeOptions": {
        "partner": "Partner",
        "child": "Kind",
        "other": "Andere"
      },
      "has_community_of_accrued_gains": "Zugewinngemeinschaft",
      "has_separate_assessment": "Getrennte Veranlagung",
      "living_in_household": "Lebt im Haushalt",
      "receives_child_support": "Unterhaltpflichtig"
    }
  }
}
</i18n>
